import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Image, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

interface GalleryViewProps {
  table: any;
}

export const GalleryView = ({ table }: GalleryViewProps) => {
  const [fields, setFields] = useState<any[]>([]);
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (table) {
      fetchFields();
      fetchRecords();
    }
  }, [table]);

  const fetchFields = async () => {
    try {
      const { data, error } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", table.id)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setFields(data || []);
    } catch (error: any) {
      toast.error("Failed to load fields");
    }
  };

  const fetchRecords = async () => {
    try {
      const { data, error } = await supabase
        .from("records")
        .select("*")
        .eq("table_id", table.id);

      if (error) throw error;
      setRecords(data || []);
    } catch (error: any) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  const getAttachmentField = () => {
    return fields.find(f => f.type === "attachment" || f.type === "url");
  };

  const getImageUrl = (record: any) => {
    const attachmentField = getAttachmentField();
    if (attachmentField) {
      const value = record.data?.[attachmentField.name];
      if (value && typeof value === "string" && (value.startsWith("http") || value.startsWith("/"))) {
        return value;
      }
    }
    return null;
  };

  const getFieldBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      single_select: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
      multi_select: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      checkbox: "bg-green-500/10 text-green-600 dark:text-green-400",
      date: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    };
    return colors[type] || "bg-muted text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4 md:p-6">
      {records.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md">
            <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Image className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No records yet</h3>
            <p className="text-muted-foreground text-sm">
              Add records to see them displayed in the gallery view
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {records.map((record) => {
            const imageUrl = getImageUrl(record);
            const titleField = fields[0];
            const displayFields = fields.slice(1, 4);
            
            return (
              <Card 
                key={record.id} 
                className="group cursor-pointer hover:shadow-xl hover:border-primary/30 transition-all duration-300 overflow-hidden border-border/50"
              >
                {/* Image/Placeholder */}
                <div className={cn(
                  "h-40 flex items-center justify-center transition-colors",
                  imageUrl ? "" : "bg-gradient-to-br from-primary/5 to-primary/10"
                )}>
                  {imageUrl ? (
                    <img 
                      src={imageUrl} 
                      alt={record.data?.[titleField?.name] || "Record"} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <FileText className="h-12 w-12 text-primary/30" />
                  )}
                </div>
                
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-base font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                    {record.data?.[titleField?.name] || "Untitled"}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="p-4 pt-0">
                  <div className="space-y-2">
                    {displayFields.map((field) => {
                      const value = record.data?.[field.name];
                      if (!value) return null;
                      
                      if (field.type === "single_select" || field.type === "multi_select") {
                        const values = Array.isArray(value) ? value : [value];
                        return (
                          <div key={field.id} className="flex flex-wrap gap-1">
                            {values.slice(0, 2).map((v: string, i: number) => (
                              <Badge 
                                key={i} 
                                variant="secondary" 
                                className={cn("text-xs", getFieldBadgeColor(field.type))}
                              >
                                {v}
                              </Badge>
                            ))}
                            {values.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{values.length - 2}
                              </Badge>
                            )}
                          </div>
                        );
                      }
                      
                      if (field.type === "checkbox") {
                        return (
                          <Badge 
                            key={field.id} 
                            variant="secondary" 
                            className={cn("text-xs", getFieldBadgeColor(field.type))}
                          >
                            {value ? "✓ " + field.name : ""}
                          </Badge>
                        );
                      }
                      
                      return (
                        <div key={field.id} className="text-xs text-muted-foreground truncate">
                          <span className="font-medium">{field.name}:</span>{" "}
                          <span>{String(value)}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
